import Styles from './SelectImage.module.scss'
import { getMediaFiles } from 'components/Design/UploadsTab/getMediaFiles'
import { useUploads } from 'components/Design/UploadsTab/useUploads'
import { SelectedImage } from 'types'
import { collectResults } from 'utils/query'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Button } from 'blaze-ui/Button'
import { Grid } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
} from 'screens/Finder/components/views/GalleryView/constants'
import { AllUploads } from 'components/ImagePickerModal/Uploads/AllUploads'
import { useModals } from 'almanac-ui/Modal'
import { DragOverlayForFirstUpload } from 'components/Design/UploadsTab/DragOverlay'
import { compact } from 'lodash'
import cx from 'classnames'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useDropzone } from 'react-dropzone'
import { useRef } from 'react'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'

interface Props {
  onSelectImage: (value: SelectedImage) => void
  lastSelectedImage?: SelectedImage
  onOpenAll?: () => void
  onSelectImageFromAll?: () => void
  isDisabled?: boolean
}

export const LibraryImages = ({
  onSelectImage,
  lastSelectedImage,
  onOpenAll,
  onSelectImageFromAll,
  isDisabled,
}: Props) => {
  const imagePickerContext = useImagePickerContext()
  const { openModal, closeModal } = useModals()
  const { selectedImages, toggleSelectedImage, accept } = imagePickerContext
  const uploadsQuery = useUploads()
  const { uploadImageFiles } = useImportMediaFiles()
  const workspaceId = useCurrentWorkspaceId()
  const { data } = useUploadsProject(workspaceId)
  const uploads = collectResults(uploadsQuery, (data) => data.files)
  const mediaFiles = getMediaFiles(uploads, accept)
  const mediaFilesAsSelectedFiles: SelectedImage[] = mediaFiles
    .map((mf) => ({ ...mf, url: mf.thumbnail_url as string }))
    .slice(0, 4)
  const inputRef = useRef<HTMLInputElement>(null)

  const onDrop = (files: File[]) => {
    if (data?.project?.id) {
      uploadImageFiles(files, data?.project.id)
      return
    }

    showToastMessage('Unable to upload file.', 'error')
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  })

  const handleOpenAll = () => {
    if (onOpenAll) {
      onOpenAll()

      return
    }

    openModal(
      AllUploads,
      {
        ...imagePickerContext,
        accept: ACCEPTED_IMAGE_MIME_TYPES,
        confirmLabel: 'Use Image',
        onPressOutside: closeModal,
        onConfirm: (images) => {
          onSelectImage(images[0])
          closeModal()
          onSelectImageFromAll?.()
        },
      },
      { newStack: true },
    )
  }

  const images = compact([lastSelectedImage].concat(mediaFilesAsSelectedFiles))
    .filter((img) => !!img.source_url)
    .slice(0, 4)

  if (uploadsQuery.isLoading || uploadsQuery.isFetching)
    return (
      <div className={Styles.imagesSection}>
        <Text variant="largeList">or from your image library</Text>
        <div className={Styles.imagesCard} {...getRootProps()}>
          <div className={Styles.imagesGrid}>
            {Array.from({ length: 4 }).map((_, i) => (
              <ShimmeringColor aspectRatio={1} width={141} height={141} key={i} />
            ))}
          </div>
          <Button frontIcon={Grid} variant="tertiary" isDisabled>
            Select another from my Image Library
          </Button>
        </div>
      </div>
    )

  return (
    <div className={Styles.imagesSection}>
      <Text variant="largeList">or from your image library</Text>
      <div className={Styles.imagesCard} {...getRootProps()}>
        {images.length > 0 ? (
          <>
            <div className={Styles.imagesGrid}>
              {images.map((file) => (
                <div
                  key={file.id}
                  className={cx(Styles.image, {
                    [Styles.isSelected]: file.id === selectedImages[0]?.id,
                    [Styles.isDisabled]: isDisabled,
                  })}
                  onClick={() => toggleSelectedImage(file)}
                >
                  <img src={file.source_url} alt="uploaded to library" />
                  {file.id === selectedImages[0]?.id && (
                    <>
                      <div className={Styles.checkbox}>
                        <Checkbox size="lg" isSelected aria-label="Selected image" />
                      </div>
                    </>
                  )}
                </div>
              ))}
              {Array.from({ length: 4 - images.length }).map((_, i) => (
                <div key={i} className={Styles.emptyPlaceholder} />
              ))}
            </div>
            <Button frontIcon={Grid} variant="tertiary" onPress={handleOpenAll} isDisabled={isDisabled}>
              Select another from my Image Library
            </Button>
          </>
        ) : (
          <>
            <DragOverlayForFirstUpload
              style={{
                minHeight: 340,
                width: 290,
                margin: 0,
                border: '3px dashed var(--dark-15, rgba(0, 0, 0, 0.15))',
                boxShadow: 'none',
              }}
              active={isDragActive}
              withSupportedFilesTag={false}
            />
            <input {...getInputProps()} ref={inputRef} />
          </>
        )}
      </div>
    </div>
  )
}
